import { Category } from '@/types/pages/storeManagement/device';
import {
    AssociationState,
    AssociateAction,
    ASSOCIATE_PAIRING_MODE,
    MultiAssociationTemp,
} from '@/types/pages/storeManagement/device/associate';
import { deriveInitInfo, deriveInitStyleInfo } from '@/utils/associateUtils';
import { fill, isNull } from 'lodash';
import { Reducer } from 'react';

export const defaultState: AssociationState = {
    loading: true,
    associateLoading: false,
    profiles: {},
    currentProfile: null,
    category: {},
    campaignEnable: false,
    productLoading: false,
    noCampaignWording: '',
    planogramConfig: {
        enable: false,
        noOption: false,
        autoSelect: false,
    },
    dialogs: {
        defaultLayout: false,
        shelf: false,
        image: false,
        pairingMode: false,
        multiProfile: false,
    },
    shelfInfo: {
        openFeature: false,
        shouldOpenDialog: false,
        options: [],
        relations: {},
    },
    imageInfo: {
        openFeature: false,
        options: [],
        relations: {},
    },
    selectedPairingMode: ASSOCIATE_PAIRING_MODE.NORMAL,

    multi: {
        styleOptions: [],
        styleInfo: {} as Category,
    },
    multiAssociateTemp: {},
    singleAssociateTemp: {
        isFinish: false,
        product: null,
        layoutIdRelations: {},
    },
};

const resetMultiAssociationTempEntry = (data: MultiAssociationTemp): MultiAssociationTemp => ({
    ...data,
    isFinish: false,
    products: [],
    layoutId: null,
    skeletonId: null,
    shelfIdRelations: {},
    imageRelations: {},
});

const getCurrentProfileIdentifier = (state: AssociationState) => state.currentProfile?.identifier;

export const reducer: Reducer<AssociationState, AssociateAction> = (state, action): AssociationState => {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'SET_PRODUCT_LOADING':
            return { ...state, productLoading: action.payload };
        case 'SET_NO_CAMPAIGN_WORDING':
            return { ...state, noCampaignWording: action.payload };
        case 'SET_ASSOCIATE_LOADING':
            return { ...state, associateLoading: action.payload };
        case 'SET_DIALOG': {
            const { key, open } = action.payload;
            return { ...state, dialogs: { ...state.dialogs, [key]: open } };
        }
        case 'SET_CURRENT_PROFILE':
            return { ...state, currentProfile: action.payload };
        case 'SET_INIT': {
            const { device, category } = action.payload;
            const {
                is_planogram_enabled,
                planogram_autoselectsingleopt,
                planogram_show_nooption,
                is_campaign_enabled,
            } = device[0] || {};
            const { multiAssociateTemp, layoutIdRelations, profiles } = deriveInitInfo(device);

            return {
                ...state,
                profiles,
                category,
                planogramConfig: {
                    ...state.planogramConfig,
                    enable: is_planogram_enabled,
                    noOption: planogram_show_nooption === '1',
                    autoSelect: planogram_autoselectsingleopt === '1',
                },
                campaignEnable: is_campaign_enabled,
                selectedPairingMode: is_campaign_enabled ? state.selectedPairingMode : ASSOCIATE_PAIRING_MODE.NORMAL,
                singleAssociateTemp: {
                    ...state.singleAssociateTemp,
                    layoutIdRelations,
                },
                multiAssociateTemp,
            };
        }
        case 'SET_SHELF_INFO': {
            return {
                ...state,
                shelfInfo: {
                    ...state.shelfInfo,
                    ...action.payload,
                },
            };
        }
        case 'SET_IMAGE_INFO': {
            return {
                ...state,
                imageInfo: {
                    ...state.imageInfo,
                    ...action.payload,
                },
            };
        }
        case 'SET_SELECTED_PAIRING_MODE':
            return {
                ...state,
                selectedPairingMode: action.payload,
            };
        case 'SET_MULTI_PAIRING_MODE': {
            const resetMultiAssociateTemp = Object.fromEntries(
                Object.entries(state.multiAssociateTemp).map(([identifier, data]) => [
                    identifier,
                    resetMultiAssociationTempEntry(data),
                ]),
            );

            return {
                ...state,
                selectedPairingMode: action.payload,
                currentProfile: null,
                multi: defaultState.multi,
                multiAssociateTemp: resetMultiAssociateTemp,
                dialogs: {
                    ...state.dialogs,
                    multiProfile: false,
                },
            };
        }
        // Single
        case 'SET_SINGLE_PRODUCT':
            return {
                ...state,
                singleAssociateTemp: {
                    ...state.singleAssociateTemp,
                    product: action.payload,
                    isFinish: !isNull(action.payload),
                },
            };
        case 'SET_SINGLE_LAYOUT_RELATIONS': {
            const { key, layoutId } = action.payload;
            return {
                ...state,
                singleAssociateTemp: {
                    ...state.singleAssociateTemp,
                    layoutIdRelations: {
                        ...state.singleAssociateTemp.layoutIdRelations,
                        [key]: layoutId,
                    },
                },
            };
        }
        case 'CLEAR_SINGLE_LAYOUT_RELATIONS': {
            const clearedRelations = Object.fromEntries(
                Object.keys(state.singleAssociateTemp.layoutIdRelations).map((key) => [key, null]),
            );

            return {
                ...state,
                singleAssociateTemp: {
                    ...state.singleAssociateTemp,
                    layoutIdRelations: clearedRelations,
                },
            };
        }
        // Multiple
        case 'SET_MULTI_CURRENT_PROFILE': {
            const profile = action.payload;
            const { styleOptions, styleInfo } = deriveInitStyleInfo(state.category, profile);
            return {
                ...state,
                currentProfile: profile,
                multi: {
                    ...state.multi,
                    styleOptions,
                    styleInfo,
                },
                multiAssociateTemp: {
                    ...state.multiAssociateTemp,
                    [profile.identifier]: {
                        ...state.multiAssociateTemp[profile.identifier],
                        products: fill(Array(styleInfo.capacity || 0), null),
                        skeletonId: styleInfo.id,
                        isFinish: false,
                    },
                },
                dialogs: {
                    ...state.dialogs,
                    multiProfile: true,
                },
            };
        }
        case 'SET_MULTI_STYLE': {
            const styleInfo = action.payload;
            const identifier = getCurrentProfileIdentifier(state);
            if (!identifier) return state;

            return {
                ...state,
                multi: {
                    ...state.multi,
                    styleInfo,
                },
                multiAssociateTemp: {
                    ...state.multiAssociateTemp,
                    [identifier]: {
                        ...state.multiAssociateTemp[identifier],
                        layoutId: null,
                        skeletonId: styleInfo.id,
                        products: fill(Array(styleInfo.capacity || 0), null),
                    },
                },
            };
        }
        case 'SET_MULTI_PRODUCT': {
            const { index, product } = action.payload;
            const identifier = getCurrentProfileIdentifier(state);
            if (!identifier) return state;

            return {
                ...state,
                multiAssociateTemp: {
                    ...state.multiAssociateTemp,
                    [identifier]: {
                        ...state.multiAssociateTemp[identifier],
                        products: state.multiAssociateTemp[identifier].products.map((o, idx) =>
                            idx === index ? product : o,
                        ),
                    },
                },
            };
        }
        case 'SET_MULTI_LAYOUT_RELATIONS': {
            const identifier = getCurrentProfileIdentifier(state);
            if (!identifier) return state;

            return {
                ...state,
                multiAssociateTemp: {
                    ...state.multiAssociateTemp,
                    [identifier]: {
                        ...state.multiAssociateTemp[identifier],
                        layoutId: action.payload,
                    },
                },
            };
        }
        case 'SET_MULTI_SHELF_RELATIONS': {
            const identifier = getCurrentProfileIdentifier(state);
            if (!identifier) return state;
            return {
                ...state,
                currentProfile: null,
                multiAssociateTemp: {
                    ...state.multiAssociateTemp,
                    [identifier]: {
                        ...state.multiAssociateTemp[identifier],
                        isFinish: true,
                        shelfIdRelations: action.payload,
                    },
                },
            };
        }
        case 'SET_MULTI_IMAGE_RELATIONS': {
            const identifier = getCurrentProfileIdentifier(state);
            if (!identifier) return state;
            return {
                ...state,
                currentProfile: null,
                multiAssociateTemp: {
                    ...state.multiAssociateTemp,
                    [identifier]: {
                        ...state.multiAssociateTemp[identifier],
                        isFinish: true,
                        imageRelations: action.payload,
                    },
                },
            };
        }
        case 'SET_MULTI_PROFILE_FINISH': {
            const identifier = getCurrentProfileIdentifier(state);
            if (!identifier) return state;
            return {
                ...state,
                currentProfile: null,
                multiAssociateTemp: {
                    ...state.multiAssociateTemp,
                    [identifier]: {
                        ...state.multiAssociateTemp[identifier],
                        isFinish: true,
                    },
                },
            };
        }
        case 'CLEAR_MULTI':
            return { ...state, multi: defaultState.multi, multiAssociateTemp: {} };
        case 'SET_CLEAR':
            return defaultState;
        default:
            return state;
    }
};
